import React from 'react';
import { isEmpty } from 'lodash';
import type { Moment } from 'moment';
import moment from 'moment';

import type { InvoiceData } from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/types';
import AutoComplete from '~/components/core/Molecules/Fields/AutoComplete';
import DatePickerTextFieldFormik from '~/components/core/Molecules/Fields/DatePickerField';
import { MonetaryValueTextField } from '~/components/core/Molecules/Fields/MonetaryValueTextField';
import TextField from '~/components/core/Molecules/Fields/TextField';
import Heading from '~/components/core/TextComponents/Heading';
import Text from '~/components/core/TextComponents/Text';
import useCurrencyFormatter from '~/components/CurrencyFormatterContext';
import DocumentIcon from '~/components/icons/DocumentIcon';

type FieldType = 'text' | 'date' | 'monetary' | 'select';

interface SummaryFieldConfig {
  title: string;
  field: keyof InvoiceData;
  type: FieldType;
  readOnly?: boolean;
  width?: 'normal' | 'wide';
  options?: Array<{ value: string; label: string }>;
}

interface SummaryProps {
  invoiceData: InvoiceData;
  onUpdate?: (field: keyof InvoiceData, value: string | number | null) => void;
  disabled?: boolean;
  shouldShowDatePerLineItem?: boolean;
}

export const Summary: React.FC<SummaryProps> = ({
  invoiceData,
  onUpdate,
  disabled,
  shouldShowDatePerLineItem = false,
}) => {
  const allFields: SummaryFieldConfig[] = [
    ...(!shouldShowDatePerLineItem
      ? [
          {
            title: 'Invoice Date',
            field: 'date' as keyof InvoiceData,
            type: 'date' as const,
          },
        ]
      : []),
    { title: 'Invoice Number', field: 'number' as keyof InvoiceData, type: 'text' as const },
    { title: 'Vendor', field: 'vendor', type: 'text', width: 'wide' },
  ];

  return (
    <div className="space-between mb-20 inline-flex w-full items-start rounded-lg bg-white p-20 ">
      {!isEmpty(invoiceData) ? (
        <DocumentIcon className="ml-4 mr-32 box-content rounded-lg bg-pink-100 stroke-pink-600 p-20" size={32} />
      ) : null}
      {allFields.map((fieldConfig, index) => (
        <SummaryItem
          key={fieldConfig.field}
          fieldConfig={fieldConfig}
          invoiceData={invoiceData}
          displayBorder={index !== 0}
          onUpdate={onUpdate}
          disabled={disabled}
        />
      ))}
    </div>
  );
};

interface SummaryItemProps {
  displayBorder: boolean;
  fieldConfig: SummaryFieldConfig;
  invoiceData: InvoiceData;
  onUpdate?: (field: keyof InvoiceData, value: string | number | null) => void;
  disabled?: boolean;
}

const SummaryItem: React.FC<SummaryItemProps> = ({ displayBorder, fieldConfig, invoiceData, onUpdate, disabled }) => {
  const { currencyFormatter } = useCurrencyFormatter();
  const { title, field, type, readOnly, width, options } = fieldConfig;
  const content = invoiceData[field];

  const renderContent = () => {
    if (disabled || readOnly) {
      return (
        <Text className="text-slate-900">
          {type === 'monetary' && content ? currencyFormatter.format(content as number) : content || ''}
        </Text>
      );
    }

    switch (type) {
      case 'date':
        return (
          <DatePickerTextFieldFormik
            id={field}
            value={content ? moment(content as string).format('YYYY-MM-DD') : null}
            disableFuture
            fullWidth
            className="[&_.MuiInput-underline:after]:border-none [&_.MuiInput-underline:before]:border-none"
            onChange={(date: Moment | null) => onUpdate?.(field, date?.format('YYYY-MM-DD') || null)}
          />
        );

      case 'monetary':
        return (
          <MonetaryValueTextField
            value={content as number}
            onChange={(value) => onUpdate?.(field, value ?? null)}
            inlineEditableField
            className="w-[100px]"
            fullWidth
          />
        );

      case 'select': {
        const selectedOption = (options || []).find((opt) => opt.value === content) || null;
        return (
          <AutoComplete
            id={field}
            options={options || []}
            value={selectedOption}
            getOptionLabel={(option) => option?.label || ''}
            onChange={(_, newValue) => onUpdate?.(field, newValue?.value || null)}
            className={width === 'wide' ? 'min-w-[300px]' : 'min-w-[130px]'}
            textFieldProps={{
              label: '',
            }}
          />
        );
      }

      case 'text':
      default:
        return (
          <TextField
            value={(content as string) || ''}
            onChange={(value) => onUpdate?.(field, value)}
            inlineEditableField
            className={width === 'wide' ? 'min-w-[300px]' : 'min-w-[130px]'}
            fullWidth
          />
        );
    }
  };

  return (
    <>
      {displayBorder ? <div className="ml-20 mr-20 border-0 border-l-1 border-solid border-slate-600 py-20" /> : null}
      <div className="p-4">
        <Heading variant={Heading.TYPES.H4} className="pb-8 text-slate-800">
          {title}
        </Heading>
        {renderContent()}
      </div>
    </>
  );
};
